import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  LinearProgress,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper
} from '@mui/material';
import {
  Psychology,
  School,
  CheckCircle,
  ExpandMore,
  Lightbulb,
  TableChart,
  AccountTree,
  Business,
  TrendingUp
} from '@mui/icons-material';
import { InterviewSession, InterviewStatus, InterviewSummary, InterviewPhase } from '../types';
import APIService from '../services/api';

interface InterviewModeProps {
  onComplete?: (summary: InterviewSummary) => void;
  onExit?: () => void;
}

const phaseLabels: Record<InterviewPhase, string> = {
  introduction: 'Introduction',
  core_tables: 'Core Tables',
  relationships: 'Relationships',
  business_rules: 'Business Rules',
  validation: 'Validation',
  complete: 'Complete'
};

export function InterviewMode({ onComplete, onExit }: InterviewModeProps) {
  const [session, setSession] = useState<InterviewSession | null>(null);
  const [currentResponse, setCurrentResponse] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState<InterviewStatus | null>(null);
  const [summary, setSummary] = useState<InterviewSummary | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startInterview = useCallback(async () => {
    try {
      setError(null);
      console.log('Starting interview...');
      const newSession = await APIService.startInterview('ceo');
      console.log('Interview session created:', newSession);
      console.log('Session currentQuestion:', newSession.currentQuestion);
      console.log('Session progress:', newSession.progress);
      
      // Ensure session has all required fields
      const safeSession = {
        ...newSession,
        currentQuestion: newSession.currentQuestion || "Hi! I'm here to learn about your SleekShop database. What's the most important table in your system?",
        progress: typeof newSession.progress === 'number' ? newSession.progress : 0,
        questionsAsked: typeof newSession.questionsAsked === 'number' ? newSession.questionsAsked : 0,
        totalQuestions: typeof newSession.totalQuestions === 'number' ? newSession.totalQuestions : 15
      };
      
      console.log('Safe session set:', safeSession);
      console.log('Final session currentQuestion:', safeSession.currentQuestion);
      console.log('Final session progress:', safeSession.progress);
      setSession(safeSession);
      await updateStatus(newSession.sessionId);
    } catch (error) {
      console.error('Failed to start interview:', error);
      setError('Failed to start interview. Please try again.');
    }
  }, []);

  useEffect(() => {
    startInterview();
  }, [startInterview]);

  // Debug logging for development
  useEffect(() => {
    if (session) {
      console.log('Interview session state:', session);
    }
    if (status) {
      console.log('Interview status state:', status);
    }
  }, [session, status]);

  const updateStatus = async (sessionId: string) => {
    try {
      console.log('Updating status for session:', sessionId);
      const currentStatus = await APIService.getInterviewStatus(sessionId);
      console.log('Status updated:', currentStatus);
      
      // Ensure all required arrays exist and progress is a number
      const safeStatus = {
        ...currentStatus,
        progress: typeof currentStatus.progress === 'number' ? currentStatus.progress : 0,
        factsLearned: currentStatus.factsLearned || [],
        tablesDiscovered: currentStatus.tablesDiscovered || [],
        relationshipsLearned: currentStatus.relationshipsLearned || [],
        businessRules: currentStatus.businessRules || [],
        insights: currentStatus.insights || [],
        estimatedTimeRemaining: typeof currentStatus.estimatedTimeRemaining === 'number' ? currentStatus.estimatedTimeRemaining : 10
      };
      
      console.log('Safe status set:', safeStatus);
      setStatus(safeStatus);
    } catch (error) {
      console.error('Failed to get interview status:', error);
      setError('Failed to update interview status.');
    }
  };

  const handleResponse = async () => {
    if (!session || !currentResponse.trim()) return;

    setIsProcessing(true);
    try {
      const response = await APIService.respondToInterview(session.sessionId, currentResponse);
      
      // Update session with new question
      setSession(prev => prev ? {
        ...prev,
        currentQuestion: response.question,
        phase: response.nextPhase || prev.phase,
        progress: response.progress,
        questionsAsked: prev.questionsAsked + 1
      } : null);

      // Also update the status with the new question and progress immediately
      setStatus(prev => prev ? {
        ...prev,
        lastQuestion: response.question,
        progress: response.progress,
        phase: response.nextPhase || prev.phase
      } : null);

      // Update status from API
      await updateStatus(session.sessionId);
      
      setCurrentResponse('');
    } catch (error) {
      console.error('Failed to respond to interview:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEndInterview = async () => {
    if (!session) return;

    try {
      const interviewSummary = await APIService.endInterview(session.sessionId);
      setSummary(interviewSummary);
      setShowSummary(true);
      
      if (onComplete) {
        onComplete(interviewSummary);
      }
    } catch (error) {
      console.error('Failed to end interview:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleResponse();
    }
  };

  if (showSummary && summary) {
    return (
      <Card sx={{ maxWidth: 800, mx: 'auto', mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CheckCircle color="success" sx={{ mr: 1 }} />
            <Typography variant="h5">Interview Complete!</Typography>
          </Box>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            {summary.summary}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <TableChart sx={{ mr: 1 }} />
              Tables Discovered ({summary.tablesDiscovered.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {summary.tablesDiscovered.map((table, index) => (
                <Chip key={index} label={table} color="primary" variant="outlined" />
              ))}
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <AccountTree sx={{ mr: 1 }} />
              Relationships Learned ({summary.relationshipsLearned.length})
            </Typography>
            <List dense>
              {summary.relationshipsLearned.map((relationship, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <TrendingUp fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={relationship} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Business sx={{ mr: 1 }} />
              Business Rules ({summary.businessRules.length})
            </Typography>
            <List dense>
              {summary.businessRules.map((rule, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Lightbulb fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={rule} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recommendations
            </Typography>
            <List dense>
              {summary.recommendations.map((rec, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Lightbulb fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={rec} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => onExit?.()}>
              Return to Chat
            </Button>
            <Button variant="outlined" onClick={() => {
              setShowSummary(false);
              startInterview();
            }}>
              Start New Interview
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ maxWidth: 600, mx: 'auto', mt: 2 }}>
        <CardContent>
          <Typography variant="h6" color="error" gutterBottom>
            Interview Error
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={startInterview}>
              Retry
            </Button>
            <Button variant="outlined" onClick={onExit}>
              Exit
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!session || !status || !status.factsLearned || !status.tablesDiscovered || !status.relationshipsLearned || !status.businessRules) {
    console.log('Loading state check:', { session: !!session, status: !!status, statusDetails: status });
    return (
      <Card sx={{ maxWidth: 600, mx: 'auto', mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Box sx={{ textAlign: 'center' }}>
              <LinearProgress sx={{ mb: 2, width: 200 }} />
              <Typography>Loading interview...</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Initializing interview data...
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  try {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Interview Header */}
        <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Psychology sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Database Interview Mode</Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            I'm learning about your SleekShop database structure through conversation. 
            This will help me provide better business insights and answers.
          </Typography>

          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                Progress: {Math.round((status?.progress ?? session?.progress) || 0)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {status?.estimatedTimeRemaining || 0} minutes remaining
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(100, Math.max(0, (status?.progress ?? session?.progress) || 0))}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {/* Phase Stepper */}
          <Stepper activeStep={status?.phase ? Object.keys(phaseLabels).indexOf(status.phase) : 0} sx={{ mt: 2 }}>
            {Object.values(phaseLabels).map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Current Question */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <School sx={{ mr: 1, color: 'primary.main' }} />
            Current Question
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
            {session?.currentQuestion || 'Loading question...'}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              value={currentResponse}
              onChange={(e) => setCurrentResponse(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your answer here..."
              disabled={isProcessing}
              variant="outlined"
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleResponse}
              disabled={isProcessing || !currentResponse.trim()}
              sx={{ minWidth: 'auto', px: 3 }}
            >
              {isProcessing ? 'Processing...' : 'Submit'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Learning Insights */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Lightbulb sx={{ mr: 1, color: 'primary.main' }} />
            What I've Learned
          </Typography>
          
                      <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">Facts & Insights ({status?.factsLearned?.length || 0})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {(status?.factsLearned || []).map((fact, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircle fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText primary={fact} />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Tables Discovered ({status?.tablesDiscovered?.length || 0})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(status?.tablesDiscovered || []).map((table, index) => (
                  <Chip key={index} label={table} color="primary" variant="outlined" />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Relationships ({status?.relationshipsLearned?.length || 0})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                {(status?.relationshipsLearned || []).map((relationship, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <TrendingUp fontSize="small" color="info" />
                    </ListItemIcon>
                    <ListItemText primary={relationship} />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Business Rules ({status?.businessRules?.length || 0})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                {(status?.businessRules || []).map((rule, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Business fontSize="small" color="warning" />
                    </ListItemIcon>
                    <ListItemText primary={rule} />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
        <Button variant="outlined" onClick={onExit}>
          Exit Interview
        </Button>
        <Button 
          variant="contained" 
          color="success"
          onClick={handleEndInterview}
          disabled={(status?.progress || 0) < 50}
        >
          Complete Interview
        </Button>
      </Box>

      {/* Progress Info */}
      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary">
          Questions answered: {session?.questionsAsked || 0} / {session?.totalQuestions || 0} • 
          Phase: {status?.phase ? phaseLabels[status.phase] : 'Loading'} • 
          Confidence: {Math.round(status?.progress || 0)}%
        </Typography>
      </Paper>
    </Box>
  );
  } catch (renderError) {
    console.error('Interview Mode render error:', renderError);
    console.error('Error details:', {
      name: (renderError as Error)?.name,
      message: (renderError as Error)?.message,
      stack: (renderError as Error)?.stack
    });
    console.error('Component state:', { session, status, error, isProcessing });
    return (
      <Card sx={{ maxWidth: 600, mx: 'auto', mt: 2 }}>
        <CardContent>
          <Typography variant="h6" color="error" gutterBottom>
            Interview Render Error
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            There was an error displaying the interview. Please try refreshing the page.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
            <Button variant="outlined" onClick={onExit}>
              Exit
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }
}
